/**
 * @fileoverview PDF Engine Types
 * @module types/pdf
 *
 * Type definitions for PDF generation engines (jsPDF and Puppeteer)
 */

import { z } from 'zod';

/**
 * PDF Generation Engine
 */
export enum PdfEngine {
	JSPDF = 'jspdf',
	PUPPETEER = 'puppeteer',
}

/**
 * Puppeteer PDF Options (HTML-to-PDF conversion)
 */
export const PuppeteerPdfOptionsSchema = z.object({
	format: z.enum(['A4', 'A3', 'Letter']).default('A4'),
	printBackground: z.boolean().default(true),
	margin: z
		.object({
			top: z.string().default('20px'),
			right: z.string().default('20px'),
			bottom: z.string().default('20px'),
			left: z.string().default('20px'),
		})
		.optional(),
	landscape: z.boolean().default(false),
	displayHeaderFooter: z.boolean().default(false),
	headerTemplate: z.string().optional(),
	footerTemplate: z.string().optional(),
	scale: z.number().min(0.1).max(2).default(1),
});

export type PuppeteerPdfOptions = z.infer<typeof PuppeteerPdfOptionsSchema>;

/**
 * HTML to PDF Request
 */
export const HtmlToPdfRequestSchema = z.object({
	html: z.string().min(1, 'HTML content is required'),
	options: PuppeteerPdfOptionsSchema.optional(),
	filename: z.string().optional(),
});

export type HtmlToPdfRequest = z.infer<typeof HtmlToPdfRequestSchema>;

/**
 * Template to PDF Request
 */
export const TemplateToPdfRequestSchema = z.object({
	templateName: z.string().min(1, 'Template name is required'),
	data: z.record(z.string(), z.unknown()),
	layoutName: z.string().optional(),
	options: PuppeteerPdfOptionsSchema.optional(),
	filename: z.string().optional(),
});

export type TemplateToPdfRequest = z.infer<typeof TemplateToPdfRequestSchema>;

/**
 * URL to PDF Request
 */
export const UrlToPdfRequestSchema = z.object({
	url: z.string().url('Valid URL is required'),
	options: PuppeteerPdfOptionsSchema.optional(),
	waitForSelector: z.string().optional(),
	timeout: z.number().int().positive().default(30000),
	filename: z.string().optional(),
});

export type UrlToPdfRequest = z.infer<typeof UrlToPdfRequestSchema>;

/**
 * PDF Generation Response
 */
export const PdfGenerationResponseSchema = z.object({
	pdfId: z.string().uuid(),
	pdfUrl: z.string(),
	message: z.string(),
	engine: z.nativeEnum(PdfEngine),
	fileSize: z.number().int().positive().optional(),
});

export type PdfGenerationResponse = z.infer<typeof PdfGenerationResponseSchema>;
